/**
 * Ürün sayfası öneri sekmeleri.
 *
 * Temanın `product-recommendations` bileşeni, getirdiği bölüm HTML'indeki ilk
 * `product-recommendations` ögesini alıyor; aynı bölümde iki panel olunca ikisi de
 * birinci paneli doldururdu. Bu yüzden panelleri burada tek tek dolduruyoruz.
 *
 * Akış: bölüm görüş alanına girince iki adres paralel çekiliyor, her yanıttan
 * `.nm-rec-payload` alınıp kendi paneline yazılıyor. Boş dönen panelin sekmesi
 * gizleniyor; ikisi de boşsa bölüm tamamen kaldırılıyor.
 *
 * ## Kaydırıcı
 *
 * Her panel kendi Swiper'ını kuruyor. `slidesPerGroup`, `slidesPerView` ile aynı
 * tutuluyor — böylece kaydırma tam sayfa ilerliyor ve sağda yarım ürün kalmıyor.
 *
 * Swiper gizli bir kapsayıcıda kurulursa genişlikleri sıfır ölçer; bu yüzden her
 * panelin kaydırıcısı **ilk kez görünür olduğunda** kuruluyor, sonrasında sekme
 * değişiminde `update()` çağrılıyor. Otomatik kayma da yalnızca görünen panelde
 * çalışıyor, arkadaki panel boşuna dönmüyor.
 */
if (!customElements.get('nm-recommendation-tabs')) {
  customElements.define(
    'nm-recommendation-tabs',
    class NmRecommendationTabs extends HTMLElement {
      connectedCallback() {
        if (this.loaded) return;
        this.loaded = true;

        this.tabs = Array.from(this.querySelectorAll('.nm-rec-tabs__tab'));
        this.panels = Array.from(this.querySelectorAll('.nm-rec-tabs__panel'));
        this.sliders = {};

        this.perView = {
          mobile: parseInt(this.dataset.columnsMobile) || 2,
          tablet: parseInt(this.dataset.columnsTablet) || 3,
          desktop: parseInt(this.dataset.columns) || 5,
        };
        this.autoplaySpeed = (parseInt(this.dataset.autoplaySpeed) || 5) * 1000;

        this.tabs.forEach((tab) => {
          tab.addEventListener('click', () => this.select(tab.dataset.tab));
        });

        if (window.FoxTheme && FoxTheme.Motion && FoxTheme.Motion.inView) {
          FoxTheme.Motion.inView(this, this.load.bind(this), { margin: '0px 0px 400px 0px' });
        } else {
          this.load();
        }
      }

      load() {
        Promise.all(this.panels.map((panel) => this.fillPanel(panel))).then(() => {
          const dolu = this.panels.filter((panel) => panel.dataset.count !== '0');

          if (dolu.length === 0) {
            this.closest('.shopify-section')?.remove();
            return;
          }

          this.tabs.forEach((tab) => {
            const panel = this.panels.find((p) => p.dataset.panel === tab.dataset.tab);
            tab.hidden = !panel || panel.dataset.count === '0';
          });

          const gorunen = this.tabs.filter((tab) => !tab.hidden);
          if (gorunen.length < 2) this.classList.add('nm-rec-tabs--single');

          this.select(gorunen[0].dataset.tab);
        });
      }

      fillPanel(panel) {
        return fetch(panel.dataset.url)
          .then((response) => (response.ok ? response.text() : Promise.reject()))
          .then((html) => {
            const payload = new DOMParser().parseFromString(html, 'text/html').querySelector('.nm-rec-payload');
            panel.dataset.count = payload ? payload.dataset.count || '0' : '0';
            if (payload) panel.innerHTML = payload.innerHTML;
          })
          .catch(() => {
            panel.dataset.count = '0';
          });
      }

      initSlider(panel) {
        const ad = panel.dataset.panel;
        if (this.sliders[ad]) {
          this.sliders[ad].slider.update();
          this.sliders[ad].slider.autoplay?.start();
          return;
        }

        const el = panel.querySelector('.nm-rec-slider');
        if (!el || !window.FoxTheme || !FoxTheme.Carousel) return;

        // Boslugu Swiper yonetiyor; CSS tarafinda `gap` sifirlandi ki iki sistem
        // catismasin. Deger burada tek yerde duruyor.
        const bosluk = window.matchMedia('(min-width: 1024px)').matches ? 24 : 16;

        const modules = [];
        if (FoxTheme.Swiper && FoxTheme.Swiper.Autoplay) modules.push(FoxTheme.Swiper.Autoplay);

        const carousel = new FoxTheme.Carousel(
          el,
          {
            // slidesPerGroup === slidesPerView: kaydırma tam sayfa ilerler,
            // sağda yarım ürün kalmaz.
            slidesPerView: this.perView.mobile,
            slidesPerGroup: this.perView.mobile,
            spaceBetween: bosluk,
            breakpoints: {
              768: { slidesPerView: this.perView.tablet, slidesPerGroup: this.perView.tablet },
              1024: { slidesPerView: this.perView.desktop, slidesPerGroup: this.perView.desktop },
            },
            // `loop` klon slaytlar uretiyor; slidesPerGroup ile birlikte kullaninca
            // sayfalar tam oturmuyor ve iki kenarda yarim urun kaliyor (olculdu).
            // `rewind` klon uretmeden sona gelince basa doner, sayfalar tam kalir.
            loop: false,
            rewind: true,
            threshold: 2,
            grabCursor: true,
            watchSlidesProgress: true,
            autoplay: { delay: this.autoplaySpeed, pauseOnMouseEnter: true, disableOnInteraction: false },
          },
          modules
        );

        carousel.init();
        this.sliders[ad] = carousel;
      }

      select(name) {
        this.tabs.forEach((tab) => {
          const secili = tab.dataset.tab === name;
          tab.classList.toggle('is-active', secili);
          tab.setAttribute('aria-selected', secili ? 'true' : 'false');
        });

        this.panels.forEach((panel) => {
          const secili = panel.dataset.panel === name;
          panel.hidden = !secili;

          if (!secili) {
            this.sliders[panel.dataset.panel]?.slider?.autoplay?.stop();
          }
        });

        const acik = this.panels.find((panel) => panel.dataset.panel === name);
        if (acik && acik.dataset.count !== '0') {
          // Panel yeni görünür oldu; genişlikler artık ölçülebilir.
          requestAnimationFrame(() => this.initSlider(acik));
        }
      }
    }
  );
}
